import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo } from 'react';
import strings from '../../../Globalization';
import Beaker from '../../../components/common/Beaker';
import DigitalGraph from '../../../components/common/DigitalGraph';
import ThermometerChart from '../../../components/common/Thermometer';
import WaterIndicator from '../../../components/common/WaterIndicatorChart';
import GaugeChart from '../../../components/common/charts/GaugeChart';
import consts from '../../../consts';
import { useCycleInfo } from '../../../contexts/CycleInfo/Context';

const Disinfection = ({ property, propertyName }) => {
	const theme = useTheme();
	const { data: cycle } = useCycleInfo();
	const data = cycle.ChartDetail[property];
	const isPAA =
		data.Disinfection.PaaORGlut.InjectedVol1 > 0 ||
		data.Disinfection.PaaORGlut.MinInjSetPt1 > 0 ||
		data.Disinfection.PaaORGlut.InjSetPt1 > 0;
	const isPAB =
		data.Disinfection.PAB.InjectedVol2 > 0 ||
		data.Disinfection.PAB.MinInjSetPt2 > 0 ||
		data.Disinfection.PAB.InjSetPt2 > 0;
	const isEnspire = cycle.EquipmentFamilyId === consts.EquipmentFamilyType.ENSPIRE;
	const waterLvlReached = Array.isArray(data?.Disinfection?.WaterLevel?.WaterLvlReached)
		? data?.Disinfection?.WaterLevel?.WaterLvlReached[0]
		: data?.Disinfection?.WaterLevel?.WaterLvlReached;
	const waterLvlReachedValue = waterLvlReached ? waterLvlReached : '';

	const upperLimit =
		data.Disinfection.Pressure.MinPressRec > data.Disinfection.Pressure.MinReqPress
			? data.Disinfection.Pressure.MinPressRec + 10
			: data.Disinfection.Pressure.MinReqPress + 10;
	const middleLimit = data.Disinfection.Pressure.MinReqPress;
	
	return (
		<Grid container>
			<Grid size={12}>
				<Card>
					<CardContent sx={{ padding: 4 }}>
						<Grid container>
							<Grid size={12} paddingTop={2}>
								<Box
									display={'flex'}
									flexDirection={'column'}
									gap={2}
									sx={{
										borderTop: (theme) => `1px solid ${theme.palette.divider}`,
										borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
										padding: (theme) => theme.spacing(1)
									}}
								>
									<Typography variant="subtitle1">
										{data.Disinfection.ThermalRinseA0 === 'None' ? '' : data.Disinfection.ThermalRinseA0}
									</Typography>
									<Typography variant="subtitle2">
										{strings[propertyName]} - {data.Disinfection.PumpType}
									</Typography>
								</Box>

								<Box>
									<Grid container>
										<Grid size={{xs: 12, md: 6, lg: 3}}>
											<ThermometerChart
												title={strings.Temperature}
												minValue={data.Disinfection.Temperature.MinTempRec}
												maxValue={data.Disinfection.Temperature.MaxTempRec}
												lowerLimit={data.Disinfection.Temperature.TempSetPt1}
												upperLimit={data.Disinfection.Temperature.MaxCycleTemp}
												unit={data.Disinfection.Temperature.Unit}
											/>
										</Grid>
										{isPAA && (
											<Grid size={{xs: 12, md: 6, lg: 3}}>
												<Beaker
													title={isPAA && isPAB ? 'PAA' : 'Glut'}
													value={data.Disinfection.PaaORGlut.InjectedVol1}
													lowerLimit={data.Disinfection.PaaORGlut.MinInjSetPt1}
													upperLimit={Number(data.Disinfection.PaaORGlut.MaxInjSetPt1)}
													unit={data.Disinfection.PaaORGlut.Unit}
													injSet={data.Disinfection.PaaORGlut.InjSetPt1}
												/>
											</Grid>
										)}
										{isPAB && (
											<Grid size={{xs: 12, md: 6, lg: 3}}>
												<Beaker
													title="PAB"
													value={data.Disinfection.PAB.InjectedVol2}
													lowerLimit={data.Disinfection.PAB.MinInjSetPt2}
													upperLimit={Number(data.Disinfection.PAB.MaxInjSetPt2)}
													unit={data.Disinfection.PAB.Unit}
													injSet={data.Disinfection.PAB.InjSetPt2}
												/>
											</Grid>
										)}
										<Grid
											size={{xs: 12, md: 6, lg: 3}}
											display={'flex'}
											alignItems={'center'}
											justifyContent={'center'}
											flexDirection={'column'}
										>
											<Typography
												textAlign={'center'}
												fontWeight={'bold'}
												variant="body2"
												gutterBottom
											>
												{strings.Pressure}
											</Typography>
											<Typography textAlign={'center'} gutterBottom>
												{data.Disinfection.Pressure.Unit}
											</Typography>
											<GaugeChart
												min={data.Disinfection.Pressure.MinLimit}
												max={Math.max(
													parseFloat(data.Disinfection.Pressure.MinPressRec),
													isEnspire
														? consts.GaugeChart.MaxScale
														: upperLimit + (upperLimit - middleLimit)
												)}
												value={data.Disinfection.Pressure.MinPressRec}
												tickCount={21}
												startAngle={-90}
												endAngle={90}
												labelTickCount={5}
												pointerLabel={data.Disinfection.Pressure.Unit}
												uom={data.Disinfection.Pressure.MinPressRec}
												segments={[
													{
														min: data.Disinfection.Pressure.MinLimit,
														max: parseFloat(data.Disinfection.Pressure.MinReqPress),
														color: theme.palette.error.light
													},
													{
														min: data.Disinfection.Pressure.MinReqPress,
														max: isEnspire
															? consts.GaugeChart.MidScale
															: parseFloat(data.Disinfection.Pressure.MiddleLimit),
														color: theme.palette.success.light
													},
													{
														min: isEnspire
															? consts.GaugeChart.MidScale
															: parseFloat(data.Disinfection.Pressure.MiddleLimit),
														max: Math.max(
															parseFloat(data.Disinfection.Pressure.MinPressRec),
															isEnspire
																? consts.GaugeChart.MaxScale
																: upperLimit + (upperLimit - middleLimit)
														),
														color: theme.palette.error.light
													}
												]}
											/>
										</Grid>
										{waterLvlReachedValue && (
											<Grid size={{xs: 12, md: 6, lg: 3}}>
												<WaterIndicator
													width="220"
													title={strings.WaterLevel}
													value={JSON.parse(data.Disinfection.WaterLevel.WaterLvlReached)}
												/>
											</Grid>
										)}
										<Grid size={{xs: 12, md: 6, lg: 3}}>
											<Typography
												textAlign={'center'}
												fontWeight={'bold'}
												variant="body2"
												gutterBottom
											>
												{strings.FillingPortPure} {data.Disinfection.Time.WaterPort}
											</Typography>
											<DigitalGraph title={strings.SetTime} value={data.Disinfection.Time.SetTime} />
											<DigitalGraph
												title={strings.RecordedTime}
												value={data.Disinfection.Time.RecordedTime}
											/>
										</Grid>
									</Grid>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};

export default memo(Disinfection);
